import jsPDF from 'jspdf';

// ── Colour palette ────────────────────────────────────────────────────────────
const TOPIC_COLORS = [
  '#3b82f6', '#f97316', '#8b5cf6', '#10b981',
  '#ef4444', '#06b6d4', '#f59e0b', '#ec4899',
  '#6366f1', '#14b8a6', '#84cc16', '#f43f5e',
];

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

// ── Flatten nested prerequisites: A. B. C. → A.1 A.2 → A.1.1 A.1.2 ─────────
function flattenPrereqs(list: any[], level = 0, parentPrefix = ''): string[] {
  const result: string[] = [];
  list.forEach((p, idx) => {
    const name = typeof p === 'object' ? p?.name : p;
    if (!name) return;

    let prefix: string;
    let indent: string;

    if (level === 0) {
      prefix = String.fromCharCode(65 + idx) + '.'; // A. B. C.
      indent = '';
    } else {
      prefix = parentPrefix + (idx + 1);            // A.1  A.2  B.1  B.2
      indent = '    '.repeat(level);
    }

    result.push(indent + prefix + ' ' + name);

    if (typeof p === 'object' && p?.children?.length) {
      result.push(...flattenPrereqs(p.children, level + 1, prefix + '.'));
    }
  });
  return result;
}

// ── Build flat rows ───────────────────────────────────────────────────────────
function buildRows(topics: any[]) {
  const rows: any[] = [];
  topics.forEach((topic, idx) => {
    const color = TOPIC_COLORS[idx % TOPIC_COLORS.length];
    (topic.subtopics || []).forEach((sub: any) => {
      const prereqs = sub.prerequisites?.length
        ? sub.prerequisites
        : topic.topic_prerequisites || [];

      const lm = (
        sub.learning_material?.length
          ? sub.learning_material
          : topic.learning_material || []
      ).join(', ') || '-';

      rows.push({
        topic: topic.topic,
        topicColor: color,
        subtopic: sub.subtopic || '-',
        description: sub.description || topic.description || '-',
        prerequisites: flattenPrereqs(prereqs).join('\n') || '-',
        learningMaterial: lm,
      });
    });
  });
  return rows;
}

// ── Wrap text into lines that fit within maxWidth ────────────────────────────
function wrapText(doc: jsPDF, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  const paragraphs = String(text).split('\n');
  for (const para of paragraphs) {
    const wrapped = doc.splitTextToSize(para, maxWidth);
    lines.push(...wrapped);
  }
  return lines;
}

// ── Main export ───────────────────────────────────────────────────────────────
export const exportTableToPdf = ({
  data,
  fileName,
  onProgress,
  onSuccess,
  onError,
}: {
  data: any;
  fileName?: string;
  onProgress?: (msg: string, type: string) => void;
  onSuccess?: (msg: string, type: string) => void;
  onError?: (msg: string, type: string) => void;
}) => {
  try {
    onProgress?.('Building PDF...', 'info');

    if (!data?.topics?.length) throw new Error('No topic data available');

    const subject = data.subject || 'Subject';
    const classNum = data.class || '';
    const board = data.board || '';
    const pdfName = fileName
      || `knowledge-map-${classNum}-${subject}-${new Date().toISOString().slice(0, 10)}.pdf`;

    const rows = buildRows(data.topics);

    // ── Page & layout constants ───────────────────────────────────────────────
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    const PAGE_W = 297;  // A4 landscape width mm
    const PAGE_H = 210;  // A4 landscape height mm
    const ML = 10;   // margin left
    const MR = 10;   // margin right
    const MT = 22;   // margin top (below title)
    const MB = 12;   // margin bottom
    const USABLE = PAGE_W - ML - MR;   // 277mm

    // Column widths
    const COL_W = [36, 46, 82, 60, 53]; // total = 277
    const HEADERS = ['Topic', 'Subtopic', 'Description', 'Prerequisites', 'Learning Materials'];
    const HDR_H = 8;   // header row height
    const PAD = 3;   // cell padding
    const LINE_H = 4.5; // mm per text line inside cell
    const FONT_SZ = 7;

    // Column x positions
    const colX: number[] = [];
    let cx = ML;
    COL_W.forEach(w => { colX.push(cx); cx += w; });

    let pageNum = 1;
    let totalPages = 1; // will patch after build

    // ── Draw page header (title + date) ──────────────────────────────────────
    const drawPageHeader = () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text(`Knowledge Map: Class ${classNum} – ${subject}  (${board})`, ML, 10);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, ML, 16);
    };

    // ── Draw table header row ────────────────────────────────────────────────
    const drawTableHeader = (y: number) => {
      const [hr, hg, hb] = [30, 64, 175]; // blue-800
      doc.setFillColor(hr, hg, hb);
      doc.rect(ML, y, USABLE, HDR_H, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONT_SZ + 0.5);
      doc.setTextColor(255, 255, 255);

      HEADERS.forEach((h, i) => {
        doc.text(h, colX[i] + COL_W[i] / 2, y + HDR_H / 2 + 1.5, { align: 'center' });
      });

      return y + HDR_H;
    };

    // ── Draw footer (page number) ─────────────────────────────────────────────
    const drawFooter = (pn: number, total: number) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text(`Page ${pn} of ${total}`, PAGE_W / 2, PAGE_H - 4, { align: 'center' });
    };

    // ── Draw a single data row, returns new Y ─────────────────────────────────
    const drawRow = (row: any, y: number, rowIdx: number): number => {
      // Calculate row height based on tallest cell
      const cellTexts = [
        row.topic,
        row.subtopic,
        row.description,
        row.prerequisites,
        row.learningMaterial,
      ];

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT_SZ);

      const lineCounts = cellTexts.map((text, i) =>
        wrapText(doc, text, COL_W[i] - PAD * 2).length
      );
      const maxLines = Math.max(...lineCounts);
      const rowH = Math.max(10, maxLines * LINE_H + PAD * 2);

      // Alternate row background
      if (rowIdx % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(ML, y, USABLE, rowH, 'F');
      } else {
        doc.setFillColor(255, 255, 255);
        doc.rect(ML, y, USABLE, rowH, 'F');
      }

      // Topic cell — coloured background
      const [tr, tg, tb] = hexToRgb(row.topicColor);
      doc.setFillColor(tr, tg, tb);
      doc.rect(colX[0], y, COL_W[0], rowH, 'F');

      // Topic text (white, bold, centred)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONT_SZ);
      doc.setTextColor(255, 255, 255);
      const topicLines = wrapText(doc, row.topic, COL_W[0] - PAD * 2);
      const topicBlockH = topicLines.length * LINE_H;
      const topicStartY = y + (rowH - topicBlockH) / 2 + LINE_H - 1;
      topicLines.forEach((line, li) => {
        doc.text(line, colX[0] + COL_W[0] / 2, topicStartY + li * LINE_H, { align: 'center' });
      });

      // Other cells — normal text, dark
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(30, 41, 59);

      [row.subtopic, row.description, row.prerequisites, row.learningMaterial].forEach((text, ci) => {
        const colIndex = ci + 1;
        const lines = wrapText(doc, text, COL_W[colIndex] - PAD * 2);
        lines.forEach((line, li) => {
          doc.text(line, colX[colIndex] + PAD, y + PAD + (li + 1) * LINE_H - 1);
        });
      });

      // Row border (bottom line)
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.3);
      doc.line(ML, y + rowH, ML + USABLE, y + rowH);

      // Vertical column dividers
      colX.forEach((x, i) => {
        if (i > 0) doc.line(x, y, x, y + rowH);
      });

      return y + rowH;
    };

    // ── Build pages ───────────────────────────────────────────────────────────
    drawPageHeader();
    let y = drawTableHeader(MT);

    // Outer border top
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.4);
    doc.rect(ML, MT, USABLE, HDR_H, 'S');

    rows.forEach((row, idx) => {
      // Estimate row height to check if it fits
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(FONT_SZ);
      const cellTexts = [row.topic, row.subtopic, row.description, row.prerequisites, row.learningMaterial];
      const maxLines = Math.max(...cellTexts.map((t, i) => wrapText(doc, t, COL_W[i] - PAD * 2).length));
      const rowH = Math.max(10, maxLines * LINE_H + PAD * 2);

      // New page if needed
      if (y + rowH > PAGE_H - MB) {
        drawFooter(pageNum, 999); // placeholder
        doc.addPage();
        pageNum++;
        drawPageHeader();
        y = drawTableHeader(MT);
      }

      y = drawRow(row, y, idx);
    });

    // Final page footer
    totalPages = doc.internal.getNumberOfPages();

    // Patch all page footers with correct total
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      drawFooter(p, totalPages);
    }

    doc.save(pdfName);
    onSuccess?.('PDF exported successfully!', 'success');
  } catch (err: any) {
    console.error('PDF export error:', err);
    onError?.(`Failed to generate PDF: ${err.message}`, 'error');
  }
};